import * as z from "zod";

// =============================================================================
// Availability (returned from getProgress to configure skip flags)
// =============================================================================

export const ShippingFulfillmentAvailabilitySchema = z.object({
  skipInternational: z.boolean().optional(),
  skipBilling: z.boolean().optional(),
  skipPackaging: z.boolean().optional(),
  skipAutomations: z.boolean().optional(),
});
export type ShippingFulfillmentAvailability = z.infer<
  typeof ShippingFulfillmentAvailabilitySchema
>;

// =============================================================================
// Enum schemas
// =============================================================================

export const OmsSystemSchema = z.enum([
  "shopify",
  "shipstation",
  "shiphero",
  "shipbob",
  "deliverr",
  "flexport",
  "ordoro",
  // cspell:disable-next-line
  "skubana",
  "fulfillment_by_amazon",
  "other",
  "none",
]);
export type OmsSystem = z.infer<typeof OmsSystemSchema>;

export const SalesChannelSchema = z.enum([
  "shopify_online_store",
  "shopify_pos",
  "amazon",
  "ebay",
  "walmart",
  "etsy",
  "facebook_instagram",
  "google_shopping",
  "tiktok",
  "wholesale",
  "other",
]);
export type SalesChannel = z.infer<typeof SalesChannelSchema>;

export const ShippingCarrierSchema = z.enum([
  "usps",
  "ups",
  "fedex",
  "dhl",
  "dhl_ecommerce",
  "canada_post",
  "royal_mail",
  "australia_post",
  "ontrac",
  "lasership",
  "amazon_shipping",
  "other",
]);
export type ShippingCarrier = z.infer<typeof ShippingCarrierSchema>;

export const PackageTypeSchema = z.enum(["box", "envelope", "soft"]);
export type PackageType = z.infer<typeof PackageTypeSchema>;

// =============================================================================
// Per-step form data schemas
// =============================================================================

export const CurrentSoftwareFormSchema = z.object({
  primarySystem: OmsSystemSchema,
  otherSystemName: z.string().optional(),
});
export type CurrentSoftwareForm = z.infer<typeof CurrentSoftwareFormSchema>;

export const ChannelsFormSchema = z.object({
  enabledChannels: z.array(SalesChannelSchema),
  primaryChannel: SalesChannelSchema.nullable(),
});
export type ChannelsForm = z.infer<typeof ChannelsFormSchema>;

export const CarrierServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
});

export const CarrierConfigSchema = z.object({
  carrier: ShippingCarrierSchema,
  enabled: z.boolean(),
  services: z.array(CarrierServiceSchema).optional(),
});

export const CarriersFormSchema = z.object({
  carriers: z.array(CarrierConfigSchema),
  hasExistingAccounts: z.boolean(),
  customCarrierAccounts: z.array(
    z.object({
      id: z.string(),
      accountName: z.string(),
      accountNumber: z.string(),
    }),
  ),
});
export type CarriersForm = z.infer<typeof CarriersFormSchema>;

export const PackageSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: PackageTypeSchema,
  dimensions: z
    .object({ length: z.number(), width: z.number(), height: z.number() })
    .optional(),
  weight: z.number().optional(),
  isDefault: z.boolean(),
  custom: z.boolean().optional(),
  carrier: z.string().nullable().optional(),
  carrierPackageName: z.string().optional(),
});

export const PackagingFormSchema = z.object({
  packages: z.array(PackageSchema),
  defaultPackageId: z.string().optional(),
});
export type PackagingForm = z.infer<typeof PackagingFormSchema>;

export const MerchantFieldsSchema = z.object({
  logo: z.boolean(),
  companyAddress: z.boolean(),
});

export const CustomerFieldsSchema = z.object({
  billingAddress: z.boolean(),
  shippingAddress: z.boolean(),
  contact: z.boolean(),
  orderNotes: z.boolean(),
  noteToCustomer: z.boolean(),
  paymentBreakdown: z.boolean(),
  paymentMethod: z.boolean(),
});

export const OrderFieldsSchema = z.object({
  orderNumber: z.boolean(),
  orderDetails: z.boolean(),
  orderBarcode: z.boolean(),
  orderDates: z.boolean(),
  packageInfo: z.boolean(),
  packageWeight: z.boolean(),
  items: z.boolean(),
  requestedShipping: z.boolean(),
  shippingMethod: z.boolean(),
  tracking: z.boolean(),
});

export const PackingSlipFormSchema = z.object({
  includePackingSlip: z.boolean(),
  showPrices: z.boolean(),
  showImages: z.boolean(),
  includeGiftMessage: z.boolean(),
  headerText: z.string().optional(),
  footerText: z.string().optional(),
  logoUrl: z.string().optional(),
  merchantFields: MerchantFieldsSchema,
  customerFields: CustomerFieldsSchema,
  orderFields: OrderFieldsSchema,
});
export type PackingSlipForm = z.infer<typeof PackingSlipFormSchema>;

export const ShippingAutomationsFormSchema = z.object({ enabled: z.boolean() });

export const OrderAutomationsFormSchema = z.object({
  autoMergeOrders: z.boolean(),
  addressVerification: z.boolean(),
});
export type OrderAutomationsForm = z.infer<typeof OrderAutomationsFormSchema>;

export const InternationalFormSchema = z.object({
  enabled: z.boolean(),
  dutiesTaxesIncluded: z.boolean(),
  aesExemptionType: z
    .enum(["itn_tax_id", "usps_exemption"])
    .nullable()
    .optional(),
  itnUsage: z
    .enum(["per_shipment", "per_item", "not_applicable"])
    .nullable()
    .optional(),
});
export type InternationalForm = z.infer<typeof InternationalFormSchema>;

export const ViewsFormSchema = z.object({ customViews: z.array(z.unknown()) });

export const BillingFormSchema = z.object({
  paymentMethodId: z.string().nullable(),
  autofundTrigger: z.number().optional(),
  autofundAmount: z.number().optional(),
  billingAddress: z
    .object({
      line1: z.string(),
      line2: z.string().optional(),
      city: z.string(),
      state: z.string(),
      zip: z.string(),
      country: z.string(),
    })
    .optional(),
  isComplete: z.boolean(),
});
export type BillingForm = z.infer<typeof BillingFormSchema>;

export const PainPointsFormSchema = z.object({
  additionalFeedback: z.string().optional(),
  biggestChallenge: z.string().optional(),
});
export type PainPointsForm = z.infer<typeof PainPointsFormSchema>;

// =============================================================================
// Completion payload schema (assembled from all step data)
// =============================================================================

export const ShippingFulfillmentCompletionPayloadSchema = z.object({
  currentSoftware: CurrentSoftwareFormSchema,
  channels: ChannelsFormSchema,
  carriers: CarriersFormSchema,
  packaging: PackagingFormSchema,
  packingSlip: PackingSlipFormSchema.optional(),
  shippingAutomations: ShippingAutomationsFormSchema,
  orderAutomations: OrderAutomationsFormSchema,
  international: InternationalFormSchema.optional(),
  views: ViewsFormSchema,
  billing: BillingFormSchema.optional(),
  painPoints: PainPointsFormSchema,
});
export type ShippingFulfillmentCompletionPayload = z.infer<
  typeof ShippingFulfillmentCompletionPayloadSchema
>;

// =============================================================================
// Default values
// =============================================================================

export const DEFAULT_PACKING_SLIP: PackingSlipForm = {
  includePackingSlip: true,
  showPrices: true,
  showImages: true,
  includeGiftMessage: false,
  merchantFields: { logo: true, companyAddress: true },
  customerFields: {
    billingAddress: false,
    shippingAddress: true,
    contact: false,
    orderNotes: false,
    noteToCustomer: false,
    paymentBreakdown: false,
    paymentMethod: false,
  },
  orderFields: {
    orderNumber: true,
    orderDetails: true,
    orderBarcode: false,
    orderDates: false,
    packageInfo: false,
    packageWeight: false,
    items: true,
    requestedShipping: false,
    shippingMethod: true,
    tracking: false,
  },
};

export const DEFAULT_ORDER_AUTOMATIONS: OrderAutomationsForm = {
  autoMergeOrders: false,
  addressVerification: true,
};

export const DEFAULT_INTERNATIONAL: InternationalForm = {
  enabled: false,
  dutiesTaxesIncluded: false,
};

export const DEFAULT_CARRIERS: CarriersForm = {
  carriers: [
    {
      carrier: "usps",
      enabled: true,
      services: [
        { id: "usps_priority", name: "Priority Mail", enabled: true },
        { id: "usps_first_class", name: "First Class Mail", enabled: true },
        {
          id: "usps_ground_advantage",
          name: "Ground Advantage",
          enabled: true,
        },
        { id: "usps_express", name: "Priority Mail Express", enabled: false },
      ],
    },
    {
      carrier: "ups",
      enabled: true,
      services: [
        { id: "ups_ground", name: "UPS Ground", enabled: true },
        { id: "ups_2nd_day_air", name: "UPS 2nd Day Air", enabled: true },
        {
          id: "ups_next_day_air",
          name: "UPS Next Day Air Saver",
          enabled: false,
        },
      ],
    },
    {
      carrier: "fedex",
      enabled: true,
      services: [
        { id: "fedex_ground", name: "FedEx Ground", enabled: true },
        {
          id: "fedex_home_delivery",
          name: "FedEx Home Delivery",
          enabled: true,
        },
        {
          id: "fedex_express_saver",
          name: "FedEx Express Saver",
          enabled: false,
        },
        { id: "fedex_2day", name: "FedEx 2Day", enabled: false },
        {
          id: "fedex_overnight",
          name: "FedEx Standard Overnight",
          enabled: false,
        },
      ],
    },
    { carrier: "dhl", enabled: false, services: [] },
    { carrier: "dhl_ecommerce", enabled: false, services: [] },
    { carrier: "canada_post", enabled: false, services: [] },
    { carrier: "royal_mail", enabled: false, services: [] },
    { carrier: "australia_post", enabled: false, services: [] },
    { carrier: "ontrac", enabled: false, services: [] },
    { carrier: "lasership", enabled: false, services: [] },
    { carrier: "amazon_shipping", enabled: false, services: [] },
    { carrier: "other", enabled: false, services: [] },
  ],
  hasExistingAccounts: false,
  customCarrierAccounts: [],
};
